/* "Często kupowane razem" — cart drawer cross-sell.
   Sourced entirely from Shopify's Product Recommendations API
   (intent=complementary), keyed off whatever is actually in the cart right
   now. Never hardcodes product/SKU data.

   Dawn replaces the cart drawer's inner DOM wholesale on several of its own
   cart-update paths — CartDrawer.renderContents() (assets/cart-drawer.js)
   nukes the whole #CartDrawer on every add-to-cart, and CartItems.updateQuantity()
   (assets/cart.js) nukes .drawer__inner on every quantity change / removal —
   both of which contain our #TichoCartCrossSell node. A reference to that
   node cached once at boot time goes stale the moment either of those runs.

   So this is a singleton controller, not a per-node instance: on every
   PUB_SUB_EVENTS.cartUpdate it re-resolves the *live* #TichoCartCrossSell
   node from the document rather than trusting anything cached earlier, and
   a monotonic run token plus a live-root identity check together make sure
   a slow/stale request never renders into a node Dawn has since replaced. */
(function () {
  'use strict';

  function formatMoney(cents, currencyCode) {
    var amount = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'pl-PL', {
        style: 'currency',
        currency: currencyCode || 'PLN',
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + (currencyCode || '');
    }
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (res) {
      if (!res.ok) throw new Error('Request failed: ' + url);
      return res.json();
    });
  }

  // Tracks which physical root nodes already have their "Dodaj zaznaczone"
  // click handler bound, so a repeated run() against the same still-live
  // root never double-attaches it. Keyed by node reference — entries fall
  // out on their own once a replaced root is no longer referenced anywhere.
  var boundRoots = new WeakSet();
  var runToken = 0;
  var pendingRun = null;

  function getRoot() {
    var root = document.getElementById('TichoCartCrossSell');
    return root && root.isConnected ? root : null;
  }

  function refreshTotal(root) {
    var rows = root._tichoRows || [];
    var totalEl = root.querySelector('#TichoCartCrossSellTotal');
    var addBtn = root.querySelector('#TichoCartCrossSellAdd');
    var currency = root.dataset.currency;
    var total = 0;
    var anyReadyToAdd = false;

    rows.forEach(function (row) {
      if (row.checked && row.variantId) {
        total += row.variant.price;
        anyReadyToAdd = true;
      }
    });

    if (totalEl) totalEl.textContent = formatMoney(total, currency);
    if (addBtn) addBtn.disabled = !anyReadyToAdd;
  }

  function render(root, products) {
    var itemsEl = root.querySelector('#TichoCartCrossSellItems');
    var currency = root.dataset.currency;
    var rows = [];
    itemsEl.innerHTML = '';

    products.forEach(function (product) {
      var hasSingleVariant = product.variants.length <= 1;
      var isAvailable = product.variants.some(function (v) {
        return v.available;
      });
      var defaultVariant = hasSingleVariant ? product.variants[0] : null;

      var row = {
        product: product,
        // Additional items start unchecked — the customer opts in deliberately,
        // rather than having to opt out of something already added to their total.
        checked: false,
        variantId: defaultVariant ? defaultVariant.id : null,
        variant: defaultVariant,
      };
      rows.push(row);

      var li = document.createElement('li');
      li.className = 'ticho-cart-cross-sell__item' + (isAvailable ? '' : ' ticho-cart-cross-sell__item--unavailable');

      var checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'ticho-cart-cross-sell__checkbox';
      checkbox.checked = row.checked;
      checkbox.disabled = !isAvailable;
      checkbox.setAttribute('aria-label', product.title);
      checkbox.addEventListener('change', function () {
        row.checked = checkbox.checked;
        refreshTotal(root);
      });

      var thumb = document.createElement('img');
      thumb.className = 'ticho-cart-cross-sell__thumb';
      thumb.loading = 'lazy';
      thumb.alt = product.title;
      thumb.src = product.featured_image
        ? product.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2')
        : '';

      var info = document.createElement('div');
      info.className = 'ticho-cart-cross-sell__info';

      var title = document.createElement('a');
      title.className = 'ticho-cart-cross-sell__title';
      title.href = product.url;
      title.textContent = product.title;
      info.appendChild(title);

      if (!isAvailable) {
        var unavailableLabel = document.createElement('span');
        unavailableLabel.className = 'ticho-cart-cross-sell__unavailable-label';
        unavailableLabel.textContent = 'Wyprzedane';
        info.appendChild(unavailableLabel);
      } else if (hasSingleVariant) {
        var price = document.createElement('span');
        price.className = 'ticho-cart-cross-sell__price';
        price.textContent = formatMoney(defaultVariant.price, currency);
        info.appendChild(price);
      } else {
        var select = document.createElement('select');
        select.className = 'ticho-cart-cross-sell__variant-select';
        var placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Wybierz wariant';
        select.appendChild(placeholder);
        product.variants.forEach(function (variant) {
          var option = document.createElement('option');
          option.value = variant.id;
          option.textContent = variant.title + ' — ' + formatMoney(variant.price, currency);
          option.disabled = !variant.available;
          select.appendChild(option);
        });
        select.addEventListener('change', function () {
          var variant = product.variants.filter(function (v) {
            return String(v.id) === select.value;
          })[0];
          row.variantId = variant ? variant.id : null;
          row.variant = variant || null;
          refreshTotal(root);
        });
        info.appendChild(select);
      }

      li.appendChild(checkbox);
      li.appendChild(thumb);
      li.appendChild(info);
      itemsEl.appendChild(li);
    });

    root._tichoRows = rows;
    refreshTotal(root);
  }

  function handleAdd(event) {
    var root = getRoot();
    // Bail quietly if the button that was clicked no longer belongs to the
    // live root — it was left over from a root Dawn has since replaced.
    if (!root || !root.contains(event.currentTarget)) return;

    var errorEl = root.querySelector('#TichoCartCrossSellError');
    var addBtn = root.querySelector('#TichoCartCrossSellAdd');
    var rows = root._tichoRows || [];

    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    var items = rows
      .filter(function (row) {
        return row.checked && row.variantId;
      })
      .map(function (row) {
        return { id: row.variantId, quantity: 1 };
      });

    if (!items.length) return;

    // Synchronous guard against rapid double-click: the button is disabled
    // before any async work starts, so a second click in the same instant
    // has nothing to act on.
    addBtn.disabled = true;
    addBtn.textContent = 'Dodawanie…';

    fetchJSON('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    })
      .then(function (response) {
        addBtn.textContent = 'Dodaj zaznaczone';
        var cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && response.sections) cartDrawer.renderContents(response);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ticho-cross-sell', cartData: response });
        }
        return run();
      })
      .catch(function () {
        addBtn.textContent = 'Dodaj zaznaczone';
        if (errorEl) {
          errorEl.textContent = 'Nie udało się dodać produktów. Spróbuj ponownie.';
          errorEl.hidden = false;
        }
        // Re-resolve rather than trust the `root`/`addBtn` captured above —
        // a cart-update side effect of the failed request could have
        // replaced the drawer DOM while the fetch was in flight.
        var liveRoot = getRoot();
        if (liveRoot) refreshTotal(liveRoot);
      });
  }

  function bindAddButton(root) {
    if (boundRoots.has(root)) return;
    boundRoots.add(root);
    var addBtn = root.querySelector('#TichoCartCrossSellAdd');
    if (addBtn) addBtn.addEventListener('click', handleAdd);
  }

  function run() {
    var root = getRoot();
    if (!root) return; // no cross-sell markup in the current DOM — nothing to do

    bindAddButton(root);

    // Monotonic token + a live-identity re-check at every async step: a
    // slower/older run resolving after a newer one (or after Dawn has
    // replaced this exact root with a fresh one) must not touch the DOM.
    var token = ++runToken;
    var isCurrent = function () {
      return token === runToken && getRoot() === root;
    };

    var errorEl = root.querySelector('#TichoCartCrossSellError');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    return fetchJSON('/cart.js')
      .then(function (cart) {
        if (!isCurrent()) return null;
        if (!cart.items.length) {
          root.hidden = true;
          return null;
        }
        root._tichoCartProductIds = cart.items.map(function (item) {
          return item.product_id;
        });
        var referenceId = cart.items[0].product_id;
        return fetchJSON(
          root.dataset.recommendationsUrl + '.json?product_id=' + referenceId + '&intent=complementary&limit=6'
        );
      })
      .then(function (data) {
        if (!isCurrent() || !data) return;
        var candidates = (data.products || [])
          .filter(function (product) {
            return root._tichoCartProductIds.indexOf(product.id) === -1;
          })
          .slice(0, 3);
        if (!candidates.length) {
          root.hidden = true;
          return;
        }
        render(root, candidates);
        root.hidden = false;
      })
      .catch(function () {
        // Recommendations are a nice-to-have, not critical path — fail quiet.
        if (isCurrent()) root.hidden = true;
      });
  }

  function scheduleRun() {
    // Several cartUpdate events can fire within the same moment (quantity
    // edits, Dawn's own add/remove handlers) — only the last one in a burst
    // needs to actually trigger a re-fetch. This is purely a request-count
    // optimization; correctness against stale/out-of-order results comes
    // from the run token above, not from this debounce.
    if (pendingRun) window.clearTimeout(pendingRun);
    pendingRun = window.setTimeout(function () {
      pendingRun = null;
      run();
    }, 300);
  }

  function boot() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'ticho-cross-sell') return; // we already re-run ourselves after our own add
        scheduleRun();
      });
    }
    run();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
